import React from 'react';
import { Box, Typography, Collapse, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';

interface CollapsibleOptionRowProps {
    optionKey: string;
    label: string;
    currentValue: string;
    isDefault?: boolean;
    isExpanded: boolean;
    onToggle: (key: string | null) => void;
    children: React.ReactNode;
    showDivider?: boolean;
}

const OptionRowHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2, 0),
    cursor: 'pointer',
    '&:hover': {
        '& .change-link': {
            color: theme.palette.primary.main,
        }
    }
}));

const CollapsibleOptionRow: React.FC<CollapsibleOptionRowProps> = ({
    optionKey,
    label,
    currentValue,
    isDefault = false,
    isExpanded,
    onToggle,
    children,
    showDivider = true
}) => {
    const handleClick = () => {
        onToggle(isExpanded ? null : optionKey);
    };

    return (
        <Box>
            {/* Collapsed Header - shows when NOT expanded */}
            {!isExpanded && (
                <OptionRowHeader onClick={handleClick}>
                    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'baseline', flexWrap: 'nowrap', gap: 0.5 }}>
                        <Typography variant="body1" fontWeight={500} sx={{ flexShrink: 0 }}>
                            {label}:
                        </Typography>
                        <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {currentValue}
                        </Typography>
                    </Box>
                    <Typography 
                        className="change-link"
                        variant="body2" 
                        sx={{ 
                            color: 'text.secondary',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            flexShrink: 0,
                            ml: 2
                        }}
                    >
                        Edit
                    </Typography>
                </OptionRowHeader>
            )}

            {/* Expanded Content - shows when expanded */}
            <Collapse in={isExpanded}>
                <Box sx={{ py: 2 }}>
                    {children}
                </Box>
            </Collapse>

            {showDivider && <Divider sx={{ borderColor: 'grey.300' }} />}
        </Box>
    );
};

export default CollapsibleOptionRow;
